import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whitelistService } from '../../services/whitelistService';
import { WhitelistEntry } from '../../types/api';

const editSchema = z.object({
  notes: z.string().optional(),
  isActive: z.boolean(),
});

type FormData = z.infer<typeof editSchema>;

interface EditWhitelistDialogProps {
  open: boolean;
  entry: WhitelistEntry;
  onClose: () => void;
}

export const EditWhitelistDialog: React.FC<EditWhitelistDialogProps> = ({
  open,
  entry,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      notes: entry.notes || '',
      isActive: entry.isActive,
    },
  });

  const isActive = watch('isActive');

  useEffect(() => {
    reset({
      notes: entry.notes || '',
      isActive: entry.isActive,
    });
  }, [entry, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => whitelistService.update(entry.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update whitelist entry');
    },
  });

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = (data: FormData) => {
    setError(null);
    updateMutation.mutate(data);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Whitelist Entry</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                IP/Prefix
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {entry.ipAddress || entry.ipPrefix}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Reason
              </Typography>
              <Typography variant="body1">{entry.reason}</Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setValue('isActive', e.target.checked)}
                />
              }
              label="Active"
            />

            <TextField
              label="Notes"
              {...register('notes')}
              error={!!errors.notes}
              helperText={errors.notes?.message}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={updateMutation.isPending}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
